import {GetStaticProps} from 'next';

import {getBlogListingPageData, getAllBlogPosts} from '@data/blogListingPage';
import getSiteConfig from '@data/siteConfig';
import Layout from '@components/Layout/Layout';
import Stretch from '@components/Layout/Stretch';
import Page from '@components/Page';
import AnnouncementBar from '@components/AnnouncementBarComponent/mapSiteConfig';
import Navigation from '@components/Navigation/mapSiteConfig';
import {Footer} from '@components/FooterComponent';
import {PageDocument, SiteConfig} from '@data/types';

import {InteriorHero, InteriorHeroProps} from '@components/InteriorHero';

import {CardGrid, CardGridProps} from '@components/CardGridComponent';

import {SanityKeyed, BlogPost, BlogListingPage} from '@schema/types';
import {ResolvedSanityReferences} from '@data/types';
import {mapLink} from '@util/mapping';
// misc
import log from '@util/logging';

// TODO: #platform: make this an extendable standard type utilizing the
// branded-type pattern centered around Article<T> ( categories, carousels,
// grids, slugs autogen, index page urls, detail page urls, etc
// all center on an article type )
interface articlePageContext {
  params: never; // no path params passed for a static page
  preview?: boolean;
}

export const getStaticProps: GetStaticProps = async (
  context: articlePageContext,
) => {
  const {preview} = context;
  // if params.slug is missing this is the root, re-map to home

  try {
    const pageData = await getBlogListingPageData(preview);

    if (!pageData) {
      return {notFound: true};
    }

    const blog = await getAllBlogPosts();

    const siteConfig = await getSiteConfig();
    return {
      props: {
        page: pageData,
        siteConfig: siteConfig,
        preview: !!preview,
        blog,
      },
    };
  } catch (e) {
    log.error(e);
    throw e;
  }
};

// TYPES
type BlogPageData = SanityKeyed<ResolvedSanityReferences<BlogListingPage>>;
type BlogData = SanityKeyed<ResolvedSanityReferences<BlogPost>>;

export const MappedInteriorHero = (block: BlogPageData) => {
  const props: InteriorHeroProps = {
    header: block.interiorHero.header,
    caption: block.interiorHero.caption,
  };

  return <InteriorHero {...props} />;
};

export const MappedCardGrid = (props: {blog: BlogData[]}) => {
  let postTypes = props.blog
    .map((t) => {
      return {
        title: t.postType.title,
        link: '#',
      };
    })
    .filter(
      (thing, index, self) =>
        index ===
        self.findIndex(
          (t) => t.title === thing.title && t.title === thing.title,
        ),
    )
    // Add sequential index
    .map((t, i) => ({
      ...t,
      id: i + 1,
    }));

  postTypes.unshift({id: 0, title: 'All Posts', link: '#'});
  const propsToPass: CardGridProps = {
    links: postTypes,
    articleCardArr: props.blog.map((n) => ({
      image: n.mainImage,
      category: n.postType.title,
      headline: n.title,
      date: n.publishedAt,
      description: n.shortDescription,
      url: `/company/community/blog/${n.slug.current}`, //TODO: extact this magic string to higher level or the slug configured in cms?
    })),
  };

  return <CardGrid {...propsToPass} />;
};

const BlogIndexPage = (props) => {
  const {page /* resources?? */} = props as {page: BlogPageData};
  const {metatitle, description, openGraphImage} = page;
  return (
    <Page {...{metatitle, description, openGraphImage}}>
      <Layout>
        <AnnouncementBar {...(props.siteConfig as SiteConfig)} />
        <Navigation {...(props.siteConfig as SiteConfig)} />
        <MappedInteriorHero {...page} />
        <MappedCardGrid {...props} />
        <Stretch />
        <Footer siteConfig={props.siteConfig as SiteConfig} />
      </Layout>
    </Page>
  );
};

export default BlogIndexPage;
